import type { AttachmentManifestV1 } from '@holdthat/crypto';

interface CurrentAttachmentManifestOptions {
  online: boolean;
  loadCached(): Promise<AttachmentManifestV1 | undefined>;
  fetchLatest(): Promise<AttachmentManifestV1>;
  saveLatest(manifest: AttachmentManifestV1): Promise<void>;
}

/**
 * Returns the authoritative manifest while online and retains the encrypted
 * local copy as a transient-network/offline fallback.
 *
 * Attachment IDs remain stable when a Hold is revised (for example when it is
 * subsequently shared with a group), so a cached manifest must never suppress
 * the online revision check.
 */
export async function currentAttachmentManifest({
  online,
  loadCached,
  fetchLatest,
  saveLatest,
}: CurrentAttachmentManifestOptions): Promise<AttachmentManifestV1 | undefined> {
  const cached = await loadCached();
  if (!online) return cached;

  try {
    const latest = await fetchLatest();
    await saveLatest(latest);
    return latest;
  } catch (error) {
    if (!cached) throw error;
    return cached;
  }
}
